import React, { useState } from "react";
import styles from "../../styles/admin.module.css";
import AdminHeader from "../../components/header";
import AdminSidebar from "../../components/sidebar";
import TableView from "../../components/table_view";
import { formatTimestamp, getTransferStatus } from "../../components/utils";
import axiosInstance from "../../../AxiosInstance";
import {
  toast,
  ToastTypes,
} from "../../../components/ajonjolib/toasts/toast/toast";
import { queryClient } from "../../../App";
import usePermissions from "../../../hooks/usePermissions";

export default function TransferManagement({ defaultStatus }) {
  const permissions = usePermissions();
  const [rows, setRows] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ quantity: "", status: "" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkEditStatus, setBulkEditStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  console.log({ defaultStatus });

  const handleEdit = (row) => {
    if (row.status === 3 || row.status === 2) {
      toast("Cannot edit completed or cancelled transfers", ToastTypes.ERROR);
      return;
    }
    setEditingRow(row.id);
    setEditForm({
      quantity: row.quantity,
      status: row.status,
    });
  };

  const handleSave = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await axiosInstance.put(
        `transfer_history/?transfer_id=${editingRow}`,
        {
          ...editForm,
          status: parseInt(editForm.status),
        }
      );
      if (response.status === 200) {
        toast("Transfer updated successfully", ToastTypes.SUCCESS);
        setRows(rows.filter((row) => row.id !== editingRow));
        setEditingRow(null);
        queryClient.invalidateQueries({
          queryKey: ["tableData-transfere-history-list"],
        });
      } else {
        toast("Error updating transfer", ToastTypes.ERROR);
      }
    } catch (error) {
      toast("Error updating transfer", ToastTypes.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkSave = async () => {
    if (isLoading || !bulkEditStatus) {
      if (!bulkEditStatus) {
        toast("Please select a status", ToastTypes.ERROR);
      }
      return;
    }

    setIsLoading(true);
    try {
      const promises = selectedRows.map((rowId) =>
        axiosInstance.put(`transfer_history/?transfer_id=${rowId}`, {
          status: parseInt(bulkEditStatus),
        })
      );

      await Promise.all(promises);
      toast("Transfers updated successfully", ToastTypes.SUCCESS);
      setRows(rows.filter((row) => !selectedRows.includes(row.id)));
      setSelectedRows([]);
      setBulkEditStatus("");
      queryClient.invalidateQueries({
        queryKey: ["tableData-transfere-history-list"],
      });
    } catch (error) {
      toast("Error updating transfers", ToastTypes.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusOptions = (currentStatus) => {
    if (permissions === 1) {
      // Toutes les options sont disponibles
      return [
        { value: "0", label: "Pending" },
        { value: "1", label: "Processing" },
        { value: "2", label: "Cancelled" },
        { value: "3", label: "Completed" },
      ];
    } else if (permissions === 2) {
      // Options limitées en fonction de l'état actuel
      if (currentStatus === 1) {
        // Si en processing, seule l'option completed est disponible
        return [
          { value: "1", label: "Processing" },
          { value: "2", label: "Cancelled" },
          { value: "3", label: "Completed" },
        ];
      } else if (currentStatus === 0) {
        // Si en pending, seule l'option processing est disponible
        return [
          { value: "0", label: "Pending" },
          { value: "1", label: "Processing" },
          { value: "2", label: "Cancelled" },
        ];
      }
      return [];
    }
    return [];
  };

  const getBulkStatusOptions = () => {
    if (permissions === 1) {
      return [
        { value: "0", label: "Pending" },
        { value: "1", label: "Processing" },
        { value: "2", label: "Cancelled" },
        { value: "3", label: "Completed" },
      ];
    } else if (permissions === 2) {
      const selectedTransfer = rows.find((row) =>
        selectedRows.includes(row.id)
      );
      if (!selectedTransfer) return [];

      if (selectedTransfer.status === 0) {
        return [{ value: "1", label: "Processing" }];
      } else if (selectedTransfer.status === 1) {
        return [{ value: "3", label: "Completed" }];
      }
      return [];
    }
    return [];
  };

  const handleRowSelection = (row) => {
    if (row.status === 3 || row.status === 2) return; // Ne pas sélectionner les transferts complétés ou annulés

    if (selectedRows.length === 0) {
      // Première sélection
      setSelectedRows([row.id]);
    } else {
      // Vérifier si le statut correspond à celui des transferts déjà sélectionnés
      const firstSelectedRow = rows.find((r) => r.id === selectedRows[0]);
      if (firstSelectedRow && firstSelectedRow.status === row.status) {
        if (selectedRows.includes(row.id)) {
          setSelectedRows(selectedRows.filter((id) => id !== row.id));
        } else {
          setSelectedRows([...selectedRows, row.id]);
        }
      } else {
        toast(
          "You can only select transfers with the same status",
          ToastTypes.ERROR
        );
      }
    }
  };

  const handleSelectAllByStatus = (status) => {
    const filteredRows = rows.filter(
      (row) => row.status === status && row.status !== 3
    );
    if (selectedRows.length === filteredRows.length) {
      // Si tous les transferts de ce statut sont déjà sélectionnés, on les désélectionne tous
      setSelectedRows(
        selectedRows.filter((id) => !filteredRows.find((row) => row.id === id))
      );
    } else {
      // Sinon, on sélectionne tous les transferts de ce statut
      const newSelectedRows = [...selectedRows];
      filteredRows.forEach((row) => {
        if (!newSelectedRows.includes(row.id)) {
          newSelectedRows.push(row.id);
        }
      });
      setSelectedRows(newSelectedRows);
    }
  };

  const cols = [
    {
      title: "Select",
      internal: "select",
      code: (row) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={() => handleRowSelection(row)}
          disabled={row.status === 3 || row.status === 2}
        />
      ),
    },
    {
      title: "Qty",
      internal: "quantity",
      code: (row, data) => {
        if (editingRow === row.id) {
          return (
            <input
              type="number"
              value={editForm.quantity}
              onChange={(e) =>
                setEditForm({ ...editForm, quantity: e.target.value })
              }
              className="form-control"
            />
          );
        }
        return <div>{data}</div>;
      },
    },
    {
      title: "Product",
      internal: "product.name",

      filter: true,
      filterInternal: "product",
    },
    {
      title: "From",
      internal: "from_warehouse.name",
      filter: true,
      filterInternal: "from_warehouse",
    },
    {
      title: "To",
      internal: "to_warehouse.name",
      filter: true,
      filterInternal: "to_warehouse",
    },

    {
      title: "Status",
      internal: "status",
      code: (row, data) => {
        if (editingRow === row.id) {
          const statusOptions = getStatusOptions(parseInt(row.status));
          return (
            <div className="d-flex align-items-center">
              <select
                value={editForm.status}
                onChange={(e) =>
                  setEditForm({ ...editForm, status: e.target.value })
                }
                className="form-control"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="ms-2">
                {getTransferStatus(parseInt(editForm.status))}
              </div>
            </div>
          );
        }
        return <div>{getTransferStatus(data)}</div>;
      },
    },
    {
      title: "Time",
      internal: "timestamp",
      code: (row, data) => {
        return <div>{formatTimestamp(data)}</div>;
      },
    },
    {
      title: "Actions",
      internal: "actions",
      code: (row) => {
        if (editingRow === row.id) {
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-success btn-sm"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                Save
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setEditingRow(null)}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          );
        }
        return (
          <button
            className={`btn btn-primary btn-sm ${
              row.status === 3 || row.status === 2 ? "disabled" : ""
            }`}
            onClick={() => handleEdit(row)}
            disabled={row.status === 3 || row.status === 2 || isLoading}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            ) : null}
            Edit
          </button>
        );
      },
    },
  ];

  return (
    <div className={styles.mainContainer}>
      <AdminHeader />
      <AdminSidebar />

      <div className={`${styles.container} me-4`}>
        <div className="mb-3 p-3 bg-light rounded">
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-primary"
              onClick={() => handleSelectAllByStatus(0)}
              disabled={!rows.some((row) => row.status === 0) || isLoading}
            >
              {isLoading ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : null}
              Select All Pending
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => handleSelectAllByStatus(1)}
              disabled={!rows.some((row) => row.status === 1) || isLoading}
            >
              {isLoading ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : null}
              Select All Processing
            </button>
          </div>
        </div>

        {selectedRows.length > 0 && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="d-flex align-items-center gap-3">
              <select
                value={bulkEditStatus}
                onChange={(e) => setBulkEditStatus(e.target.value)}
                className="form-control"
                style={{ width: "200px" }}
                disabled={isLoading}
              >
                <option value="">Select Status</option>
                {getBulkStatusOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                onClick={handleBulkSave}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : null}
                Update Selected ({selectedRows.length})
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedRows([]);
                  setBulkEditStatus("");
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <TableView
          fetchUrl={"transfer_history"}
          pull={setRows}
          defaultFilters={
            defaultStatus !== undefined ? { status: defaultStatus } : {}
          }
          // noPagination={false}
          limit={defaultStatus !== undefined ? 500 : 90}
          title={
            defaultStatus === 0
              ? "Pending Transfers"
              : defaultStatus === 1
              ? "Processing Transfers"
              : "Transfer History"
          }
          cols={cols}
          rows={rows}
          withIndex={true}
          noCreate={true}
          useReactQuery={`transfere-history-list${
            defaultStatus !== undefined ? `-${defaultStatus}` : ""
          }`}
        />
      </div>
    </div>
  );
}
